import type Phaser from "phaser";
import { aimCone, denseNext, easeHeading, easeStrength, inCone, newSightCone, type AssetManifest } from "daedalus-core";
import { SolidMasks, fadeOverCharacter, type BodyBox, type BodySize, type ConeSight, type CoveringPiece, type KeptCover, type PieceAlpha, type PropView } from "daedalus-renderer-phaser";
import { BODY_HEIGHT, BODY_WIDTH, OCCLUDERS, OCCLUDER_MIN_HEIGHT, SIGHT_CONE, type SightConeRule } from "odyssey-shared";
import { piecesCoveringCharacter } from "./cover.ts";
import type { PresenceProps } from "./presence.ts";

/**
 * A character's body box, which the occluder fade tests (ADR-040).
 */
const CHARACTER_BODY: BodySize = { width: BODY_WIDTH, height: BODY_HEIGHT };

/**
 * The props that fade over a character (ADR-040), the cone of sight ahead of it (ADR-061's amendment for P1.9-T75),
 * and the cover and fade reports built on the same rule (ADR-071 decisions 9 and 10), over every prop view the world
 * draws: the chunk painter's, the things', and for the reports the presence props'. The cone's axis follows the
 * character's heading along the shorter arc (`SIGHT_CONE.turnMs`); it is aimed each frame into one kept object and
 * the props in it eased in the fade's own walk, so the cone adds no walk and allocates nothing.
 *
 * The cone acts only in a dense place (ADR-061's amendment for P1.9-T82). The cover of a square around the character,
 * the art of the props the cone could dim over the square's area, is measured when the character has moved a tile
 * since the last measurement and otherwise every `dense.refreshMs`, never per frame: one read-only walk of each view.
 * The place turns dense and open with the rule's hysteresis, the cone's strength eases between 0 and 1 each frame, and
 * the fade is handed the strength; at none it tests no prop against the cone. An entity is in sight while the cone
 * has any strength: a prop the cone holds is see-through from its first step below whole, as P1.9-T75 rules. At a
 * map's edge the square is cut to the map, so the part that hangs off it does not thin the cover.
 */
export class PropFade {
  private readonly body: BodyBox = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private readonly sight: ConeSight;
  private readonly coneAngle: number;
  private heading = Number.NaN;
  private cover = 0;
  private dense = false;
  private coverX = Number.NaN;
  private coverY = Number.NaN;
  private coverAgeMs = 0;
  private readonly other: BodyBox = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private readonly keptMarks = new Uint8Array(BODY_WIDTH * BODY_HEIGHT);
  private keptShare = 0;
  private roofShare = 0;
  private concealMasks: SolidMasks | null;
  private coverMasks: SolidMasks | null = null;

  /**
   * @param textures The texture manager the atlas pages are in, for masks built on demand.
   * @param assets The world's asset manifest.
   * @param masks The atlas's solid pixel masks, or null when they could not be read, which stops props fading
   *   over the character.
   * @param props The prop views by key.
   * @param presence The presence props.
   * @param tileSize The world's tile size in pixels, by which the cone's reach in tiles is measured.
   * @param rule The sight cone's knobs; the game's unless a test hands its own.
   * @param bounds The map's size in world pixels, to which the measured square is cut; absent, it is never cut.
   */
  public constructor(
    private readonly textures: Phaser.Textures.TextureManager,
    private readonly assets: AssetManifest,
    private masks: SolidMasks | null,
    private readonly props: ReadonlyMap<string, PropView>,
    private readonly presence: PresenceProps,
    private readonly tileSize: number,
    private readonly rule: SightConeRule = SIGHT_CONE,
    private readonly bounds?: { readonly width: number; readonly height: number },
  ) {
    this.concealMasks = masks;
    this.sight = { cone: newSightCone(), rule, elapsedMs: 0, strength: 0 };
    this.coneAngle = (rule.angle * Math.PI) / 180;
  }

  /**
   * Fades every prop that covers the local character this frame, and eases the props in its cone of sight: the cone
   * is aimed at the feet along the heading eased toward `heading`, first snapped to it, and its strength eased toward
   * 1 where the place is dense and 0 where it is open (`measureCover`, when due). A fade that throws stops the
   * fade, and the cone's with it, for the rest of the session with one warning; the cone is still aimed, so the
   * concealment's `inSight` keeps answering. The same body box is then marked by every prop that never fades and
   * draws above the character (`keptCover`, ADR-040's amendment of 2026-10-03), which the local character's
   * silhouette reads; with no masks it is 0. While building parts hide any of it and the character stands in no
   * presence area, the body is also measured against the presence props (`roofCover`): a closed roof drawn over it.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @param depth The character sprite's depth.
   * @param heading The way the character walks or faces, in radians (`headingOf`).
   * @param elapsedMs Time since the last frame.
   */
  public fadeFor(x: number, y: number, depth: number, heading: number, elapsedMs: number): void {
    const first = Number.isNaN(this.heading);
    this.heading = first ? heading : easeHeading(this.heading, heading, elapsedMs, this.rule.turnMs);
    aimCone(this.sight.cone, x, y, this.heading, this.coneAngle, this.rule.reach * this.tileSize);
    this.sight.elapsedMs = elapsedMs;
    this.coverAgeMs += elapsedMs;
    if (first || this.coverAgeMs >= this.rule.dense.refreshMs || Math.abs(x - this.coverX) >= this.tileSize || Math.abs(y - this.coverY) >= this.tileSize) {
      this.measureCover(x, y);
    }
    this.sight.strength = easeStrength(this.sight.strength, this.dense, elapsedMs, this.rule.dense.easeMs);
    this.keptShare = 0;
    this.roofShare = 0;
    const masks = this.masks;
    if (masks === null) {
      return;
    }
    try {
      fadeOverCharacter(this.props.values(), this.body, CHARACTER_BODY, x, y, depth, OCCLUDERS, masks.maskOf, undefined, this.sight);
      const marks = this.keptMarks;
      marks.fill(0);
      let marked = 0;
      for (const view of this.props.values()) {
        marked += view.markKept(this.body, depth, masks.maskOf, marks);
      }
      this.keptShare = marked / marks.length;
      if (marked > 0 && !this.presence.insideAny) {
        let roof = 0;
        for (const view of this.presence.propViews) {
          roof = view.mostCovered(this.body, depth, OCCLUDER_MIN_HEIGHT, masks.maskOf, roof);
        }
        this.roofShare = roof;
      }
    } catch (error) {
      this.keptShare = 0;
      this.roofShare = 0;
      this.masks = null;
      console.warn(`props stop fading over the character this session: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Measures the cover around a feet point and judges the place dense or open by it (ADR-061's amendment for
   * P1.9-T82): the art of the props the cone could dim (`PropView.coneCover`, summed over the views) inside the square
   * of `dense.radius` tiles to each side, cut to the map where it hangs off it, over the area of what is left, then
   * the rule's hysteresis; a square wholly off the map has no cover. One walk of each view's props; nothing is
   * allocated.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   */
  private measureCover(x: number, y: number): void {
    const reach = this.rule.dense.radius * this.tileSize;
    const bounds = this.bounds;
    const left = bounds === undefined ? x - reach : Math.max(0, x - reach);
    const top = bounds === undefined ? y - reach : Math.max(0, y - reach);
    const right = bounds === undefined ? x + reach : Math.min(bounds.width, x + reach);
    const bottom = bounds === undefined ? y + reach : Math.min(bounds.height, y + reach);
    let area = 0;
    if (right > left && bottom > top) {
      for (const view of this.props.values()) {
        area += view.coneCover(left, top, right, bottom, this.rule.minHeight);
      }
      area /= (right - left) * (bottom - top);
    }
    this.cover = area;
    this.dense = denseNext(this.dense, this.cover, this.rule.dense);
    this.coverX = x;
    this.coverY = y;
    this.coverAgeMs = 0;
  }

  /**
   * The cover around the local character at the last measurement: the art of the props the cone could dim inside the
   * square around its feet, as cut to the map, over that area. It passes 1 where canopies overlap.
   *
   * @returns The cover, 0 before the first frame.
   */
  public get sightCover(): number {
    return this.cover;
  }

  /**
   * Whether the place around the local character is dense, by the last measurement and the rule's hysteresis: the
   * state the cone's strength eases toward.
   *
   * @returns True in a dense place.
   */
  public get sightDense(): boolean {
    return this.dense;
  }

  /**
   * The cone of sight's strength as eased this frame: 0 in an open place, 1 in a dense one.
   *
   * @returns The strength in `[0, 1]`.
   */
  public get sightStrength(): number {
    return this.sight.strength;
  }

  /**
   * The share of the local character's body the props that never fade, building parts, hid together while drawn
   * above it at the last `fadeFor`: the union of their solid pixels over the body box, so a wall, a door and a porch
   * that each hide part of it count as one cover. The silhouette shows by it.
   *
   * @returns The share in `[0, 1]`.
   */
  public get keptCover(): number {
    return this.keptShare;
  }

  /**
   * The largest share of the local character's body one closed roof, a presence prop drawn above it, hid at the last
   * `fadeFor` (ADR-040's amendment of 2026-10-03, item 7): measured only while building parts hide some of the body
   * and the character stands in no presence area, since inside one its roof is open and judged by its closed frame
   * would seem to cover it. Above 0 the silhouette is withheld: a closed roof hides the character as before.
   *
   * @returns The share in `[0, 1]`.
   */
  public get roofCover(): number {
    return this.roofShare;
  }

  /**
   * The largest share of another character's body one prop hides while drawn above it (ADR-061's amendment for
   * P1.9-T73): the fade's measure, by the same masks and the same height floor, over the prop views the fade is
   * handed (never the presence props), counting only props drawn whole, so one faded over the local character is
   * see-through. The body box is `width` pixels centred on the feet and `height` pixels standing on the feet line,
   * refilled in one object. A measure that throws stops concealment by props for the rest of the session with one
   * warning, as the fade stops; with no masks every answer is 0. With `kept`, the same walk raises `kept.share` to
   * the largest share one prop the cone never dims hides, a building part or a thing, which still conceals a
   * character in the cone of sight.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @param width The body's width in pixels.
   * @param height The body's height in pixels, above the feet line.
   * @param depth The depth the character's sprite draws at.
   * @param kept The share props the cone never dims give, raised in place; absent, it is not measured.
   * @returns The share in `[0, 1]`.
   */
  public mostCovered(x: number, y: number, width: number, height: number, depth: number, kept?: KeptCover): number {
    const masks = this.concealMasks;
    if (masks === null) {
      return 0;
    }
    const box = this.other;
    box.x0 = x - Math.floor(width / 2);
    box.y0 = y - height;
    box.x1 = box.x0 + width - 1;
    box.y1 = y - 1;
    try {
      let best = 0;
      for (const view of this.props.values()) {
        best = view.mostCovered(box, depth, OCCLUDER_MIN_HEIGHT, masks.maskOf, best, kept);
      }
      return best;
    } catch (error) {
      this.concealMasks = null;
      console.warn(`props stop concealing other characters this session: ${error instanceof Error ? error.message : String(error)}`);
      return 0;
    }
  }

  /**
   * Whether a feet point stands in the local character's cone of sight as aimed this frame, while the cone acts: it
   * has any strength (ADR-061's amendment for P1.9-T82), so an entity is revealed from the first step of the ease in,
   * when the props ahead turn see-through, until the ease out ends, and in an open place nothing is in sight. Before
   * the first frame it holds nothing.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @returns True when the cone holds it.
   */
  public inSight(x: number, y: number): boolean {
    return this.sight.strength > 0 && inCone(this.sight.cone, x, y);
  }

  /**
   * The cone of sight's axis as eased this frame, in radians, or NaN before the first frame.
   *
   * @returns The heading.
   */
  public get sightHeading(): number {
    return this.heading;
  }

  /**
   * How many props the cone of sight holds below full alpha, summed over the prop views. Reading it allocates nothing.
   *
   * @returns The count.
   */
  public get sightDimmed(): number {
    let sum = 0;
    for (const view of this.props.values()) {
      sum += view.dimmed;
    }
    return sum;
  }

  /**
   * A count that moves whenever a prop the concealment measures could cover a character differently: the sum of the
   * prop views' versions (`PropView.version`: a prop synced in, out or elsewhere, or an alpha the fade changed) and how
   * many views there are. Reading it allocates nothing.
   *
   * @returns The count.
   */
  public get version(): number {
    let sum = this.props.size;
    for (const view of this.props.values()) {
      sum += view.version;
    }
    return sum;
  }

  /**
   * The masks the reports judge pieces by: the atlas's, else masks built once from the loaded textures.
   *
   * @returns The masks.
   */
  private reportMasks(): SolidMasks {
    this.coverMasks ??= this.masks ?? new SolidMasks(this.textures, this.assets);
    return this.coverMasks;
  }

  /**
   * The pieces that would cover a character standing at a feet point (ADR-071 decision 9, `cover.ts`): every prop,
   * presence prop and thing the page draws now, judged by its own frame at the depth its image draws at, against
   * the depth a character sprite on that feet line draws at. Read-only: nothing moves.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @param depth The depth a character on that feet line draws at.
   * @returns The covering pieces.
   */
  public coveringAt(x: number, y: number, depth: number): CoveringPiece[] {
    const masks = this.reportMasks();
    return piecesCoveringCharacter([...this.props.values(), ...this.presence.propViews], { x, y }, depth, masks.maskOf);
  }

  /**
   * The alpha every prop, presence prop and thing draws at once the occluder fade has run for a character standing
   * at a feet point (ADR-071 decision 10): the fade the frame runs for the local character, run here at that point
   * and depth, over the same views, with the cone of sight handed in at no elapsed time so every cone alpha stays
   * where it is. Every prop is drawn whole again once the alphas are read, and the next frame
   * fades for the local character as before; nothing moves.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @param depth The depth a character on that feet line draws at.
   * @returns Each shown piece and its alpha.
   * @throws RangeError when the feet are off whole pixels and a prop reaches the pixel count.
   */
  public fadingAt(x: number, y: number, depth: number): PieceAlpha[] {
    const masks = this.reportMasks();
    const elapsed = this.sight.elapsedMs;
    this.sight.elapsedMs = 0;
    try {
      fadeOverCharacter(this.props.values(), this.body, CHARACTER_BODY, x, y, depth, OCCLUDERS, masks.maskOf, undefined, this.sight);
    } finally {
      this.sight.elapsedMs = elapsed;
    }
    const alphas = [...this.props.values(), ...this.presence.propViews].flatMap((view) => view.alphas());
    for (const view of this.props.values()) {
      view.restoreAlpha();
    }
    return alphas;
  }
}
